/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { EmployeeActions } from './employee.actions';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
} from 'firebase/firestore';
import { FIRESTORE } from 'src/app/common/firebase/firebase.tokens';
import { Action } from '@ngrx/store';
import { Employee } from '@enrollhere/classes';

/**
 * @deprecated NgRx effects with direct Firestore access are deprecated. Use API endpoints instead.
 */
@Injectable()
export class EmployeeEffects {
  private actions$ = inject(Actions);
  /** @deprecated Direct Firestore access is deprecated. Use API endpoints instead. */
  private firestore = inject(FIRESTORE);

  private toError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
  }

  private mapEmployeeFromDoc(docSnapshot: {
    id: string;
    data: () => Record<string, unknown>;
  }) {
    return Employee.fromJSON({ ...docSnapshot.data(), id: docSnapshot.id });
  }

  private emitLoadEmployeesSuccess(
    subscriber: { next: (action: Action) => void },
    snapshot: {
      docs: Array<{ id: string; data: () => Record<string, unknown> }>;
    }
  ) {
    const employees = snapshot.docs.map(docSnapshot =>
      this.mapEmployeeFromDoc(docSnapshot)
    );
    subscriber.next(EmployeeActions.loadEmployeesSuccess({ employees }));
  }

  private emitLoadEmployeesFailure(
    subscriber: { next: (action: Action) => void },
    error: unknown
  ) {
    subscriber.next(
      EmployeeActions.loadEmployeesFailure({ error: this.toError(error) })
    );
  }

  private emitLoadEmployeeSuccess(
    subscriber: { next: (action: Action) => void },
    snapshot: { id: string; data: () => Record<string, unknown> }
  ) {
    const employee = Employee.fromJSON({
      ...snapshot.data(),
      id: snapshot.id,
    });
    subscriber.next(EmployeeActions.loadEmployeeSuccess({ employee }));
  }

  private emitLoadEmployeeFailure(
    subscriber: { next: (action: Action) => void },
    error: unknown
  ) {
    subscriber.next(
      EmployeeActions.loadEmployeeFailure({ error: this.toError(error) })
    );
  }

  private createAddEmployeeSuccess(employee: Employee, id: string) {
    return EmployeeActions.addEmployeeSuccess({
      employee: { ...employee, id },
    });
  }

  private createRemoveEmployeeSuccess(employeeId: string) {
    return EmployeeActions.removeEmployeeSuccess({ employeeId });
  }

  private createUpdateEmployeeSuccess(employeeId: string, employee: Employee) {
    return EmployeeActions.updateEmployeeSuccess({
      employeeId,
      employee,
    });
  }

  loadEmployees$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EmployeeActions.loadEmployees),
      switchMap(() => {
        return new Observable<Action>(subscriber => {
          const unsubscribe = onSnapshot(
            collection(this.firestore, 'employees'),
            snapshot => {
              this.emitLoadEmployeesSuccess(
                subscriber,
                snapshot as {
                  docs: Array<{
                    id: string;
                    data: () => Record<string, unknown>;
                  }>;
                }
              );
            },
            error => {
              this.emitLoadEmployeesFailure(subscriber, error);
            }
          );

          // Provide a way of canceling and disposing the listener
          return unsubscribe;
        }).pipe(
          catchError(error =>
            of({ type: '[Employee API] Load Employees Error', error })
          )
        );
      })
    );
  });

  loadEmployee$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EmployeeActions.loadEmployee),
      switchMap(({ employeeId }) => {
        return new Observable<Action>(subscriber => {
          console.log('loadEmployee$', employeeId);
          const unsubscribe = onSnapshot(
            doc(this.firestore, 'employees', employeeId),
            snapshot => {
              this.emitLoadEmployeeSuccess(
                subscriber,
                snapshot as {
                  id: string;
                  data: () => Record<string, unknown>;
                }
              );
            },
            error => {
              this.emitLoadEmployeeFailure(subscriber, error);
            }
          );
          return unsubscribe;
        }).pipe(
          catchError(error =>
            of({ type: '[Employee API] Load Employee Error', error })
          )
        );
      })
    );
  });

  addEmployee$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EmployeeActions.addEmployee),
      mergeMap(async ({ employee }) => {
        try {
          const docRef = await addDoc(
            collection(this.firestore, 'employees'),
            Employee.toJSON(employee) as any
          );
          return this.createAddEmployeeSuccess(employee, docRef.id); // return new employee with id
        } catch (error) {
          return EmployeeActions.addEmployeeFailure({ error: error as Error });
        }
      })
    );
  });

  removeEmployee$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EmployeeActions.removeEmployee),
      mergeMap(async ({ employeeId }) => {
        try {
          await deleteDoc(doc(this.firestore, 'employees', employeeId));
          return this.createRemoveEmployeeSuccess(employeeId); // return removed employee's id
        } catch (error) {
          return EmployeeActions.removeEmployeeFailure({
            error: error as Error,
          });
        }
      })
    );
  });

  updateEmployee$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EmployeeActions.updateEmployee),
      mergeMap(async ({ employeeId, employee }) => {
        try {
          await setDoc(
            doc(this.firestore, 'employees', employeeId),
            Employee.toJSON(employee) as any,
            {
              merge: true,
            }
          );
          return this.createUpdateEmployeeSuccess(employeeId, employee); // return updated employee's id and changes
        } catch (error) {
          return EmployeeActions.updateEmployeeFailure({
            error: error as Error,
          });
        }
      })
    );
  });
}
